import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { AosDirective } from '../../shared/directives/aos.directive';
import { CategoriesCarouselBlockComponent } from './blocks/categories-carousel-block/categories-carousel-block.component';
import { ComunidadBlockComponent } from './blocks/comunidad-block/comunidad-block.component';
import { EventosBlockComponent } from './blocks/eventos-block/eventos-block.component';
import {
  FeaturedCategoriesBlockComponent,
  FeaturedCategoriesBlockData,
} from './blocks/featured-categories-block/featured-categories-block.component';
import {
  FeaturedCategoryBlockComponent,
  FeaturedCategoryBlockData,
} from './blocks/featured-category-block/featured-category-block.component';
import { FeaturedProductBlockComponent } from './blocks/featured-product-block/featured-product-block.component';
import { HeroComponent } from './blocks/hero/hero.component';
import { HeroBannerBlockComponent } from './blocks/hero-banner-block/hero-banner-block.component';
import { environment } from '../../../environments/environment';
import { CategoryItem } from './data/mock-categories';
import { MOCK_HERO_DATA } from './data/mock-hero';
import { HeroCarouselData } from './models/hero.model';
import { HeroService } from './data/hero.service';
import {
  NewReleasesBlockComponent,
  NewReleasesBlockData,
} from './blocks/new-releases-block/new-releases-block.component';
import { OffersBlockComponent, OffersBlockData } from './blocks/offers-block/offers-block.component';
import {
  PerronSinglesBlockComponent,
  PerronSinglesBlockData,
} from './blocks/perron-singles-block/perron-singles-block.component';
import {
  TopSellersBlockComponent,
  TopSellersBlockData,
} from './blocks/top-sellers-block/top-sellers-block.component';
import { AddToCartEvent, Product } from '../../shared/models/product.model';
import { cartErrorMessage } from '../../shared/utils/cart.utils';
import { ProductZoomModalComponent } from '../../shared/ui/product-zoom-modal/product-zoom-modal.component';
import { StatsBlockComponent } from './blocks/stats-block/stats-block.component';
import { CartService } from '../../shared/services/cart.service';
import { CartDrawerService } from '../../shared/services/cart-drawer.service';
import { PrepurchaseModalService } from '../../shared/services/prepurchase-modal.service';
import { StockAlertModalService } from '../../shared/services/stock-alert-modal.service';
import { StockAlertStore } from '../../shared/services/stock-alert.store';
import { ToastService } from '../../shared/services/toast.service';
import { resolvePdpCategorySlug } from '../catalog/data/catalog-mappers';
import { HomeBlocksService } from './data/home-blocks.service';
import {
  PublicHeroBannerData,
  PublicHomeBlock,
  PublicStatsBannerData,
} from './data/home-blocks.model';
import {
  publicCategoryToCarouselItem,
  publicOfferProductToUiProduct,
} from '../../shared/utils/home-blocks.utils';

type CommunityBlockData = Extract<PublicHomeBlock, { type: 'community' }>['data'];

type HomeBlocksState =
  | { status: 'loading'; blocks: PublicHomeBlock[] }
  | { status: 'loaded'; blocks: PublicHomeBlock[] }
  | { status: 'error'; blocks: PublicHomeBlock[] };

// Items que el template renderiza, ya mapeados al view-model que cada bloque
// espera. Unión discriminada por `kind` para que el `@switch` en la plantilla
// no necesite type guards.
type HomeRenderItem =
  | { kind: 'categories-carousel'; data: CategoryItem[] }
  | { kind: 'offers'; data: OffersBlockData }
  | { kind: 'new-arrivals'; data: NewReleasesBlockData }
  | { kind: 'best-sellers'; data: TopSellersBlockData }
  | { kind: 'subcategories-carousel'; data: PerronSinglesBlockData }
  | { kind: 'featured-categories-pair'; data: FeaturedCategoriesBlockData }
  | { kind: 'featured-category'; data: FeaturedCategoryBlockData }
  | { kind: 'featured-product'; data: Product }
  | { kind: 'hero-banner'; data: PublicHeroBannerData }
  | { kind: 'stats-banner'; data: PublicStatsBannerData }
  | { kind: 'community'; data: CommunityBlockData };

@Component({
  selector: 'app-home',
  imports: [
    HeroComponent,
    HeroBannerBlockComponent,
    CategoriesCarouselBlockComponent,
    FeaturedCategoriesBlockComponent,
    FeaturedCategoryBlockComponent,
    FeaturedProductBlockComponent,
    OffersBlockComponent,
    NewReleasesBlockComponent,
    PerronSinglesBlockComponent,
    TopSellersBlockComponent,
    StatsBlockComponent,
    ComunidadBlockComponent,
    EventosBlockComponent,
    ProductZoomModalComponent,
    AosDirective,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomePageComponent {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cartService = inject(CartService);
  private readonly cartDrawerService = inject(CartDrawerService);
  private readonly prepurchaseModalService = inject(PrepurchaseModalService);
  private readonly stockAlertModal = inject(StockAlertModalService);
  private readonly stockAlertStore = inject(StockAlertStore);
  private readonly toastService = inject(ToastService);
  private readonly homeBlocksService = inject(HomeBlocksService);
  private readonly heroService = inject(HeroService);

  protected readonly homeSkeletonRows: readonly number[] = [0, 1];
  protected readonly homeSkeletonProducts: readonly number[] = [0, 1, 2, 3, 4, 5];
  protected readonly homeSkeletonTabs: readonly number[] = [0, 1, 2, 3];

  // Estado de `/web/home`: mientras carga se muestran skeletons, y en error
  // se ocultan los bloques API-driven para evitar datos mock en producción.
  private readonly blocksState = toSignal(
    this.homeBlocksService.getHome().pipe(
      map((blocks): HomeBlocksState => ({ status: 'loaded', blocks })),
      catchError((err) => {
        console.error('[home] failed to load blocks', err);
        return of<HomeBlocksState>({ status: 'error', blocks: [] });
      }),
    ),
    { initialValue: { status: 'loading', blocks: [] } as HomeBlocksState },
  );

  protected readonly homeBlocksLoading = computed(
    () => this.blocksState().status === 'loading',
  );

  private readonly blocksSig = computed(() => this.blocksState().blocks);

  // Render items en el orden del backend. Cada bloque del API se proyecta al
  // shape que su componente espera; los tipos sin componente API-driven (hero,
  // featured-categories-pair, stats-banner, community, featured-category,
  // featured-product, hero-banner) caen en `null` y el template los omite.
  protected readonly renderItems = computed<HomeRenderItem[]>(() => {
    const items: HomeRenderItem[] = [];
    for (const block of this.blocksSig()) {
      const item = toRenderItem(block);
      if (item) items.push(item);
    }
    return items;
  });

  // Categorías raíz derivadas del primer bloque `categories-carousel` del API.
  // Las usa `resolvePdpCategorySlug` para construir URLs canónicas al navegar
  // desde una card o el zoom — sin esto, las rutas quedan rotas.
  private readonly rootCategoriesForRouting = computed(() => {
    const carousel = this.blocksSig().find((b) => b.type === 'categories-carousel');
    if (!carousel) return [];
    return carousel.data.categories
      .filter((c) => !!c.slug)
      .map((c) => ({
        id: c.id,
        name: c.name,
        slug: c.slug as string,
        description: null,
        cover: c.cover,
        is_expansion: false,
        parent_id: null,
      }));
  });

  // Carrusel hero del top-of-page. La fuente es `/api/hero-carousel` (distinto
  // de `/api/web/home`, que orquesta los bloques mid-page). `initialValue` con
  // `loading: true` activa el skeleton mientras llega la respuesta; en error,
  // dev cae al mock para preservar la pantalla durante desarrollo, prod queda
  // vacío para no enmascarar el problema y no degradar LCP con un mock cargado.
  protected readonly heroData = toSignal(
    this.heroService.getHero().pipe(
      catchError((err) => {
        console.error('[home/hero] fetch failed', err);
        const fallback: HeroCarouselData = environment.production
          ? { loading: false, slides: [] }
          : MOCK_HERO_DATA;
        return of(fallback);
      }),
    ),
    {
      initialValue: { loading: true, slides: [] } as HeroCarouselData,
    },
  );

  protected zoomProduct = signal<Product | null>(null);
  /** Campana del quickview: misma fuente que la card (`StockAlertStore`). */
  protected readonly zoomNotifyActive = computed(() =>
    this.stockAlertStore.hasAlert(this.zoomProduct()?.id),
  );

  // ── Quickview lifecycle ──────────────────────────────────────────────────
  protected onZoomOpen(product: Product): void {
    this.zoomProduct.set(product);
  }

  protected onZoomClose(): void {
    this.zoomProduct.set(null);
  }

  /** "Leer más" / "Ver ficha completa" del quickview → cierra y navega. */
  protected onViewDetails(product: Product): void {
    this.zoomProduct.set(null);
    this.onCardClick(product);
  }

  // ── Carrito (bloques + zoom) ─────────────────────────────────────────────
  protected onAddToCart(event: AddToCartEvent): void {
    // Defensa en profundidad (D25): una precompra nunca entra al carrito
    // normal (la API responde 422); va al modal, como desde el zoom.
    if (event.product.isPreSale) {
      this.prepurchaseModalService.open(event.product, event.quantity);
      return;
    }
    this.addLineToCart(event);
  }

  protected onZoomAddToCart(event: AddToCartEvent): void {
    // Precompra desde el zoom: abrir el modal con la cantidad elegida, sin
    // tocar el carrito. Los productos regulares sí van al carrito normal.
    if (event.product.isPreSale) {
      this.zoomProduct.set(null);
      this.prepurchaseModalService.open(event.product, event.quantity);
      return;
    }
    this.zoomProduct.set(null);
    this.addLineToCart(event);
  }

  // ── Precompra ────────────────────────────────────────────────────────────
  protected onReserve(product: Product): void {
    this.prepurchaseModalService.open(product);
  }

  protected onZoomReserve(event: AddToCartEvent): void {
    this.zoomProduct.set(null);
    this.prepurchaseModalService.open(event.product, event.quantity);
  }

  // ── Notify me (stock alerts) ─────────────────────────────────────────────
  protected onNotify(product: Product): void {
    this.stockAlertModal.open(product);
  }

  protected onNotifyCancel(product: Product): void {
    this.stockAlertModal.cancel(product);
  }

  // ── Navegación a PDP ─────────────────────────────────────────────────────
  // Slug autoritativo del backend: derivamos la categoría desde
  // `product.categories[0]` (parent preferido sobre la categoría hoja). Si el
  // producto no trae categorías — caso patológico —, no navegamos.
  protected onCardClick(product: Product): void {
    if (!product.slug) return;
    const categorySlug = resolvePdpCategorySlug(product, this.rootCategoriesForRouting());
    if (!categorySlug) return;
    this.router.navigate(['/tienda', categorySlug, product.slug]);
  }

  // ── Helpers privados ─────────────────────────────────────────────────────
  private addLineToCart(event: AddToCartEvent): void {
    this.cartService
      .addLine(Number(event.product.id), event.quantity, event.product)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.cartDrawerService.open(),
        error: (err) => {
          // `null` = bloqueo de Acceso VIP: el diálogo ya lo explica.
          const message = cartErrorMessage(err, 'No pudimos agregar el producto al carrito.');
          if (message) this.toastService.show({ type: 'error', message });
        },
      });
  }
}

// ── Mapper de bloque API → render item ────────────────────────────────────

function toRenderItem(block: PublicHomeBlock): HomeRenderItem | null {
  switch (block.type) {
    case 'categories-carousel': {
      const items = block.data.categories
        .map(publicCategoryToCarouselItem)
        .filter((x): x is CategoryItem => x !== null);
      if (items.length === 0) return null;
      return { kind: 'categories-carousel', data: items };
    }
    case 'offers': {
      const products = block.data.products.map(publicOfferProductToUiProduct);
      if (products.length === 0) return null;
      return {
        kind: 'offers',
        data: { sort_by: block.data.sort_by, limit: block.data.limit, products },
      };
    }
    case 'best-sellers': {
      const products = block.data.products.map(publicOfferProductToUiProduct);
      if (products.length === 0) return null;
      return {
        kind: 'best-sellers',
        data: { limit: block.data.limit, products },
      };
    }
    case 'new-arrivals': {
      if (block.data.sections.length === 0) return null;
      return {
        kind: 'new-arrivals',
        data: {
          limit_per_category: block.data.limit_per_category,
          sections: block.data.sections,
          sectionsUiProducts: block.data.sections.map((s) =>
            s.products.map(publicOfferProductToUiProduct),
          ),
        },
      };
    }
    case 'subcategories-carousel': {
      if (block.data.sections.length === 0) return null;
      return {
        kind: 'subcategories-carousel',
        data: {
          parent: block.data.parent,
          limit_per_subcategory: block.data.limit_per_subcategory,
          sections: block.data.sections,
          sectionsUiProducts: block.data.sections.map((s) =>
            s.products.map(publicOfferProductToUiProduct),
          ),
        },
      };
    }
    case 'featured-categories-pair':
      return {
        kind: 'featured-categories-pair',
        data: { left: block.data.left, right: block.data.right },
      };
    case 'featured-category': {
      const products = block.data.products.map(publicOfferProductToUiProduct);
      if (products.length === 0) return null;
      return {
        kind: 'featured-category',
        data: { mode: block.data.mode, category: block.data.category, products },
      };
    }
    case 'featured-product':
      return {
        kind: 'featured-product',
        data: publicOfferProductToUiProduct(block.data.product),
      };
    case 'hero-banner':
      return { kind: 'hero-banner', data: block.data };
    case 'stats-banner':
      return { kind: 'stats-banner', data: block.data };
    case 'community':
      return { kind: 'community', data: block.data };
  }
}
